/**
 * IF YOU DO ANY CHANGE TO THIS REACT PLAYER WRAPPER. ALSO UPDATE IT'S DOCUMENTATION
 * LOCATED IN `prd-player-js/docs/api-react.md`.
 *
 * THANKS BROTHER.
 */
import React, { useRef, useState, useEffect, useCallback, PropsWithChildren } from 'react';
import { IAd, IErrorEvent, ISource, PlayerBase } from '@24i/player-base';
import { usePrevious, useEventListener } from '@24i/player-utils-react';
import { View } from 'react-native';
import { useEventsManager } from '../utils/events';
// import { log } from '@24i/nxg-core-utils/src/logger';

import { IPlayer } from './types';
import { PlayerContainer, VideoContainer, PlayerUI } from './styled';

export const Player = ({
    engine,
    source,
    autoPlay,
    startPosition,
    startPositionType,
    quality,
    abrStrategy,
    onError,
    onAdBreakStart,
    onAdBreakEnd,
    onEnd,
    onBitrateChange,
    onPlay,
    onPause,
    onSeeking,
    onSeekingOutOfDVR,
    onSeeked,
    children,
    setVideoPlayerRef,
    isCheckingBlockers,
    hasBlockingReason = false,
    fullscreenContainerOverride = null,
}: PropsWithChildren<IPlayer>) => {
    const playerContainer = useRef<HTMLDivElement>(null);
    const fullscreenContainer = useRef<HTMLDivElement>(null);
    const [videoPlayer, setVideoPlayer] = useState<PlayerBase>(null);
    const prevSource: ISource | null = usePrevious(source);
    const prevVideoPlayer = usePrevious(videoPlayer || undefined);
    const prevEngine = usePrevious(engine);
    const destroyRef = useRef<() => void>();
    // TODO: refactor this madness
    const setVideoPlayerRefRef = useRef<(PlayerBase) => void>(setVideoPlayerRef);
    setVideoPlayerRefRef.current = setVideoPlayerRef;
    const [isEmbeddedAdUiShown, setIsEmbeddedAdUiShown] = useState(false);

    /*
     * ---------- EVENTS ----------
     */

    useEventsManager(
        {
            onPlay,
            onPause,
            onSeeking,
            onSeekingOutOfDVR,
            onSeeked,
            onEnd,
            onAdBreakStart,
            onAdBreakEnd,
            onBitrateChange,
        },
        videoPlayer
    );

    const onVideoContainerClick = useCallback(() => {
        /*
         * This handles the case when embedded UI flag turns true before the
         * Ads UI is ready to handle the click events.
         */
        if (isEmbeddedAdUiShown && videoPlayer?.paused && videoPlayer.currentTime === 0) {
            videoPlayer.play();
        }
    }, [videoPlayer, isEmbeddedAdUiShown]);

    /**
     * Error listener, which propagates the event to the corresponding prop callback.
     */
    const onErrorHandler = useCallback(
        (event: IErrorEvent) => {
            if (onError) onError(event.error);
        },
        [onError]
    );

    /**
     * Adds all needed event listeners to the provided player instance.
     *
     * @param playerInstance
     */
    const addEventListeners = (playerInstance: PlayerBase) => {
        playerInstance.addEventListener('error', onErrorHandler);
    };

    /**
     * Removes all needed event listeners from the provided player instance.
     *
     * @param playerInstance
     */
    const removeEventListeners = (playerInstance: PlayerBase) => {
        playerInstance.removeEventListener('error', onErrorHandler);
    };

    /*
     * ---------- Lifecycle ----------
     */
    /**

    /**
     * Destroy the player.
     */
    const destroy = useCallback((): void => {
        if (!videoPlayer) {
            return;
        }
        videoPlayer.destroy();
        setVideoPlayer(null);
        if (setVideoPlayerRefRef.current) {
            setVideoPlayerRefRef.current(null);
        }
    }, [videoPlayer]);
    destroyRef.current = destroy;

    /**
     * Unmount the player.
     */
    useEffect(() => () => destroyRef.current?.(), []);

    /**
     * Detect engine switch. Destroy already existing engine and build a new one.
     */
    useEffect(() => {
        if (
            !engine ||
            !playerContainer.current ||
            !fullscreenContainer.current ||
            isCheckingBlockers
        )
            return;
        if (engine === prevEngine) {
            // log('same engine');
            if (videoPlayer) {
                // log('videoPlayer exists');
                const didUrlChange = source?.url !== prevSource?.url;
                if (source !== prevSource) {
                    try {
                        videoPlayer.source = source;
                    } catch (error) {
                        onError(error);
                        destroy();
                    }
                    if (didUrlChange) {
                        videoPlayer.load().catch((error) => {
                            onError(error);
                            destroy();
                        });
                    }
                }
                videoPlayer.autoPlay = autoPlay;
                videoPlayer.startPosition = startPosition;
                videoPlayer.startPositionType = startPositionType;
                videoPlayer.abrStrategy = abrStrategy;
                videoPlayer.quality = quality;
                return;
            }
        }
        destroy();
        const newVideoPlayer = engine.createPlayer(
            {
                source,
                autoPlay,
                startPosition,
                startPositionType,
                abrStrategy,
                quality,
            },
            playerContainer.current,
            fullscreenContainerOverride?.current || fullscreenContainer.current
        );


        if (prevVideoPlayer && prevVideoPlayer !== newVideoPlayer) {
            removeEventListeners(prevVideoPlayer);
        }

        // Add listeners to the new engine
        addEventListeners(newVideoPlayer);
        // log('initializePlayer');
        setVideoPlayer(newVideoPlayer);
        // Notify client that engine was changed
        if (setVideoPlayerRefRef.current) setVideoPlayerRefRef.current(newVideoPlayer);
        newVideoPlayer.initializePlayer();
        // newVideoPlayer.mediaLoad();
    }, [engine, source, prevSource, isCheckingBlockers]);

    useEventListener(
        'adstarted',
        useCallback((e: IAd) => {
            setIsEmbeddedAdUiShown(e.embeddedUi === true);
        }, []),
        videoPlayer
    );
    useEventListener(
        'adbreakended',
        useCallback(() => setIsEmbeddedAdUiShown(false), []),
        videoPlayer
    );

    return (
        <PlayerContainer ref={fullscreenContainer}>
            <VideoContainer
                testID="playing_content"
                data-test="player-video-container"
                onClick={onVideoContainerClick}
                ref={playerContainer}
            />
            <PlayerUI data-test="player-ui-container" {...{ isEmbeddedAdUiShown }}>
                {(videoPlayer || hasBlockingReason) &&
                    typeof children === 'function' &&
                    children(videoPlayer)}
            </PlayerUI>
        </PlayerContainer>
    );
};
